import { useState, useEffect } from "react";
import moment from "moment-timezone";
import { v4 as uuidV4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, X } from "lucide-react";
import { PdfField } from "../types";
import { DEFAULT_FONT_SIZE } from "./common";
import { safeParseDecimal } from "@/lib/api/common";
import { Checkbox } from "@/components/ui/checkbox";
import { acceptedFieldsOptions, AcceptedDestinationPacketFields, signerOptions, SignerType } from "@/lib/api/destinationPackets";

interface FieldFormProps {
  field: PdfField;
  onSave: (updates: Partial<PdfField>) => void;
  onCancel: () => void;
  availableFields: AcceptedDestinationPacketFields[];
}

export function FieldForm({ field, onSave, onCancel, availableFields }: FieldFormProps) {
  // Tracked lender fields that must be assigned to PRIMARY_CUSTOMER
  const TRACKED_LENDER_FIELDS: readonly string[] = [
    AcceptedDestinationPacketFields.LENDER_NAME,
    AcceptedDestinationPacketFields.LENDER_EMAIL,
  ];
  const isFieldTrackedLender = TRACKED_LENDER_FIELDS.includes(field.field_id);

  const [formData, setFormData] = useState({
    selectedFieldType: field.field_type === 'system' ? (field.field_id as AcceptedDestinationPacketFields) : '',
    field_id: field.field_id,
    field_key: field.field_key || acceptedFieldsOptions.find(opt => opt.value === field.field_id)?.label || field.field_id,
    field_type: field.field_type,
    system_field_type: field.system_field_type || 'system_reference_field',
    field_dest_type: field.field_dest_type,
    // Ensure tracked lender fields default to PRIMARY_CUSTOMER if signer_id is missing
    signer_id: isFieldTrackedLender 
      ? (field.signer_id || SignerType.PRIMARY_CUSTOMER)
      : (field.signer_id || ''),

    position_data: {
      x: safeParseDecimal({})(field.position_data.x).times(100).toNumber(),
      y: safeParseDecimal({})(field.position_data.y).times(100).toNumber(),
      width: safeParseDecimal({})(field.position_data.width).times(100).toNumber(),
      height: safeParseDecimal({})(field.position_data.height).times(100).toNumber(),

      text_size: field.position_data.text_size || DEFAULT_FONT_SIZE,
      page: field.position_data.page || 1,
    },

    is_required: field.is_required ?? true,
    is_readonly: field.is_readonly ?? false,

    freeform_value: field.freeform_value,
    value: field.value || '',
  });

  // Compute field type flags from formData, not the original field
  const isSystemField = formData.field_type === 'system' as const;
  const isSignatureField = formData.field_type === 'signature' as const;

  // Tracked signature fields that must preserve their exact IDs
  const TRACKED_SIGNATURE_FIELDS: readonly string[] = [
    AcceptedDestinationPacketFields.LENDER_NAME,
    AcceptedDestinationPacketFields.LENDER_EMAIL,
  ];

  const isTrackedSignatureField = (fieldId: string): boolean => {
    return TRACKED_SIGNATURE_FIELDS.includes(fieldId);
  };

  const isCurrentFieldTracked = isTrackedSignatureField(formData.field_id);

  // Auto-generate field_id for signature fields when field_key changes
  // Only regenerate if the field_key has changed and the current ID doesn't match the pattern
  // BUT skip regeneration for tracked signature fields (they must preserve their exact IDs)
  useEffect(() => {
    if (formData.field_type === 'signature' && formData.field_key && !isCurrentFieldTracked) {
      const sanitizedKey = formData.field_key.toLowerCase().replace(/[^a-z0-9]/g, '_');
      const expectedIdPrefix = `signature_${sanitizedKey}_`;
      
      // Only regenerate if the current ID doesn't match the expected pattern for this field_key
      // This prevents regenerating when editing existing fields
      if (!formData.field_id.startsWith(expectedIdPrefix)) {
        const newFieldId = `${expectedIdPrefix}${uuidV4().slice(0, 8)}`;
        setFormData(prev => ({ ...prev, field_id: newFieldId }));
      }
    }
  }, [formData.field_key, isSignatureField, isCurrentFieldTracked]); // Removed field_id from dependencies to prevent infinite loop

  const availableFieldOptions = acceptedFieldsOptions.filter(option => 
    availableFields.includes(option.value) || option.value === formData.selectedFieldType
  );

  const getDefaultFieldDestType = (fieldId: string): "signature" | "signature_initials" | "date_signed" | "checkbox" | "text" | "attachment" | "note" | undefined => {
    const lowerFieldId = fieldId.toLowerCase();

    if (/date/i.test(lowerFieldId)) {
      return "date_signed";
    }

    if (/signature/i.test(lowerFieldId)) {
      return "signature";
    }

    if (/initials/i.test(lowerFieldId)) {
      return "signature_initials";
    }

    if (/attachment/i.test(lowerFieldId)) {
      return "attachment";
    }

    if (/checkbox/i.test(lowerFieldId)) {
      return "checkbox";
    }

    if (/note/i.test(lowerFieldId)) {
      return "note";
    }

    return "text";
  };

  const handleFieldSelection = (selectedField: string) => {
    // Check if it's a system field
    // System fields are just fields the system knows how to fill in before sending out for signing
    // Lender fields for Destination Packets are not system fields but we DO need the id to be knowable for lender email
    // So we can extract that info from signature output and reassign the signing recipient to the correct lender (as you have to put an email
    // For each recipient at the time of sending. but we dont know the lender at that point. so we re-assign the signer for the lender later)
    const isSelectedSystemField = [AcceptedDestinationPacketFields.CUSTOMER_NAME, AcceptedDestinationPacketFields.CUSTOMER_EMAIL].includes(selectedField as AcceptedDestinationPacketFields);
    const isTrackedLenderField = [
      AcceptedDestinationPacketFields.LENDER_NAME,
      AcceptedDestinationPacketFields.LENDER_EMAIL,
    ].includes(selectedField as AcceptedDestinationPacketFields);
    const selectedOption = acceptedFieldsOptions.find(option => option.value === selectedField);
    const text_size = generateTextSizeFrom(selectedField);
    const preview_value = "";
    const defaultFieldDestType = !isSelectedSystemField ? getDefaultFieldDestType(selectedField) : undefined;

    setFormData(prev => {
      // Tracked lender fields must be assigned to PRIMARY_CUSTOMER signer
      // (primary customer fills in lender info)
      const defaultSignerId = isTrackedLenderField ? SignerType.PRIMARY_CUSTOMER : (!isSelectedSystemField ? prev.signer_id : '');

      return {
        ...prev,
        value: preview_value,
        freeform_value: "",
        position_data: {
          ...prev.position_data,
          text_size,
        },
        selectedFieldType: selectedField,
        field_id: selectedField,
        field_key: selectedOption?.label || selectedField,
        field_type: isSelectedSystemField ? 'system' : 'signature',
        system_field_type: isSelectedSystemField ? 'enum_fixed_value' as const : prev.system_field_type,
        field_dest_type: defaultFieldDestType,
        is_required: !isSelectedSystemField ? true : prev.is_required,
        signer_id: defaultSignerId,
      };
    });
  };

  const handleSignatureFieldNameChange = (fieldKey: string) => {
    setFormData(prev => {
      // Don't allow changing field_key for tracked signature fields (they must preserve their IDs)
      if (isTrackedSignatureField(prev.field_id)) {
        return prev;
      }

      const sanitizedKey = fieldKey.toLowerCase().replace(/[^a-z0-9]/g, '_');
      const prevSanitizedKey = prev.field_key.toLowerCase().replace(/[^a-z0-9]/g, '_');
      const expectedIdPrefix = `sig_${sanitizedKey}_`;
      
      // Only regenerate ID if the sanitized key actually changed
      // This prevents regenerating on every keystroke when editing
      let newFieldId = prev.field_id;
      if (sanitizedKey !== prevSanitizedKey) {
        // Sanitized key changed, generate new ID
        newFieldId = `${expectedIdPrefix}${uuidV4().slice(0, 8)}`;
      } else if (!prev.field_id.startsWith(expectedIdPrefix)) {
        // Current ID doesn't match pattern, generate new one
        newFieldId = `${expectedIdPrefix}${uuidV4().slice(0, 8)}`;
      }
      // Otherwise, keep the existing ID
      
      return {
        ...prev,
        field_key: fieldKey,
        field_id: newFieldId,
      };
    });
  };

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (isSystemField && !formData.selectedFieldType) {
      newErrors.selectedFieldType = "Please select a field type";
    }

    if (isSignatureField && !formData.field_key) {
      newErrors.field_key = "Please enter a field name";
    }

    if (isSignatureField && !formData.signer_id) {
      newErrors.signer_id = "Please select a signer";
    }

    if (isSignatureField && !formData.field_dest_type) {
      newErrors.field_dest_type = "Please select a destination type";
    }

    if (formData.field_type === 'system' && (formData.position_data.text_size < 6 || formData.position_data.text_size > 20)) {
      newErrors.text_size = "Font size must be between 6 and 20";
    }

    if (formData.position_data.x < 0 || formData.position_data.x > 100) {
      newErrors.x = "X position must be between 0 and 100%";
    }

    if (formData.position_data.y < 0 || formData.position_data.y > 100) {
      newErrors.y = "Y position must be between 0 and 100%";
    }

    if (formData.position_data.width <= 0 || formData.position_data.width > 100) {
      newErrors.width = "Width must be between 1 and 100%";
    }

    if (formData.position_data.height <= 0 || formData.position_data.height > 100) {
      newErrors.height = "Height must be between 1 and 100%";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const updates: Partial<PdfField> = {
      field_id: formData.field_id,
      field_key: formData.field_key,
      field_type: formData.field_type,
      system_field_type: formData.system_field_type,
      // Clear signature-specific fields for system fields
      field_dest_type: formData.field_type === 'system' ? undefined : formData.field_dest_type,
      signer_id: formData.field_type === 'system' ? undefined : (formData.signer_id || undefined),
      position_data: {
        x: formData.position_data.x / 100,
        y: formData.position_data.y / 100,
        width: formData.position_data.width / 100,
        height: formData.position_data.height / 100,
        page: formData.position_data.page,
        text_size: formData.position_data.text_size,
      },
      freeform_value: formData.freeform_value,
      value: formData.value,
      is_required: formData.is_required,
      is_readonly: formData.is_readonly,
    };

    onSave(updates);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSave();
    } else if (event.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div className="p-3 border-2 border-blue-300 rounded-lg bg-blue-50 space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-sm">Edit Field</h4>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={handleSave}>
            <Check className="w-3 h-3 text-green-600" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="w-3 h-3 text-red-600" />
          </Button>
        </div>
      </div>
      {formData.field_type === 'signature' && (
        <>
          <div className="grid gap-2">
            <Label htmlFor="field_key">Field Name</Label>
            <Input
              id="field_key"
              value={formData.field_key}
              onChange={(e) => handleSignatureFieldNameChange(e.target.value)}
              placeholder="Enter field name..."
              required
              disabled={isCurrentFieldTracked}
              className={errors.field_key ? 'border-red-500' : ''}
            />
            {errors.field_key && (
              <p className="text-xs text-red-500">{errors.field_key}</p>
            )}
            <p className="text-xs text-gray-500">
              Field ID: {formData.field_id}
              {isCurrentFieldTracked && " (Tracked - ID cannot be changed)"}
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="signer_id">Signer</Label>
            <Select
              value={formData.signer_id}
              onValueChange={(value: string) =>
                setFormData(prev => ({ ...prev, signer_id: value }))
              }
              disabled={isCurrentFieldTracked}
            >
              <SelectTrigger className={errors.signer_id ? 'border-red-500' : ''} disabled={isCurrentFieldTracked}>
                <SelectValue placeholder="Select signer..." />
              </SelectTrigger>
              <SelectContent>
                {signerOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.signer_id && (
              <p className="text-xs text-red-500">{errors.signer_id}</p>
            )}
            {isCurrentFieldTracked && (
              <p className="text-xs text-gray-500">
                Tracked lender fields are automatically assigned to Primary Customer
              </p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="field_dest_type">Destination Type</Label>
            <Select
              value={formData.field_dest_type || "text"}
              onValueChange={(value: "signature" | "signature_initials" | "date_signed" | "checkbox" | "text" | "attachment" | "note") =>
                setFormData(prev => ({ ...prev, field_dest_type: value }))
              }
            >
              <SelectTrigger className={errors.field_dest_type ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select destination type..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="signature">Signature</SelectItem>
                <SelectItem value="signature_initials">Initials</SelectItem>
                <SelectItem value="date_signed">Date</SelectItem>
                <SelectItem value="checkbox">Checkbox</SelectItem>
                <SelectItem value="note">Text Area</SelectItem>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="attachment">Attachment</SelectItem>
              </SelectContent>
            </Select>
            {errors.field_dest_type && (
              <p className="text-xs text-red-500">{errors.field_dest_type}</p>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className="grid gap-2">
              <Label className="cursor-pointer">
                Required
              </Label>
              <Checkbox
                checked={formData.is_required}
                onCheckedChange={(value: boolean) => setFormData(prev => ({ ...prev, is_required: value }))}
                className="rounded border-gray-300"
              />
            </div>
            <div className="grid gap-2">
              <Label className="cursor-pointer">
                Readonly
              </Label>
              <Checkbox
                checked={formData.is_readonly}
                onCheckedChange={(value: boolean) => setFormData(prev => ({ ...prev, is_readonly: value }))}
                className="rounded border-gray-300"
              />
            </div>
          </div>
        </>
      )}
      <div className="space-y-3 md:space-y-4">
        <div className="flex flex-col md:grid md:grid-cols-3 gap-4">
          <div className="flex flex-col">
            <Label className="text-xs">Field Type</Label>
            <Combobox
              placeholder="Select field type..."
              options={availableFieldOptions}
              value={formData.selectedFieldType}
              onValueChange={handleFieldSelection}
              dwimOnChange
              className={`mt-1 ${errors.selectedFieldType ? 'border-red-500' : ''}`}
            />
            {errors.selectedFieldType && (
              <p className="text-xs text-red-500 mt-1">{errors.selectedFieldType}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {formData.field_type === 'system'
                ? 'Text will be inserted by the system'
                : 'Select a system field above, or leave blank for signature field'}
            </p>
          </div>
        </div>

        {formData.field_type === 'system' && (
          <div className="grid gap-2">
            <Label htmlFor="text_size">Font Size</Label>
            <Input
              id="text_size"
              type="number"
              min="6"
              max="20"
              value={formData.position_data.text_size}
              onChange={(e) => setFormData(prev => ({ ...prev, position_data: { ...prev.position_data, text_size: parseInt(e.target.value) || DEFAULT_FONT_SIZE } }))}
              className={`mt-1 ${errors.text_size ? 'border-red-500' : ''}`}
            />
            {errors.text_size && (
              <p className="text-xs text-red-500 mt-1">{errors.text_size}</p>
            )}
          </div>
        )}

        {/* Position and Size Controls */}
        <div>
          <Label className="text-xs font-medium mb-2 block">Position & Size</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-gray-600">X (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.position_data.x.toFixed(1)}
                onChange={(e) => setFormData(prev => ({ ...prev, position_data: { ...prev.position_data, x: parseFloat(e.target.value) || 0 } }))}
                onKeyDown={handleKeyPress}
                className={`mt-1 ${errors.x ? 'border-red-500' : ''}`}
              />
              {errors.x && (
                <p className="text-xs text-red-500 mt-1">{errors.x}</p>
              )}
            </div>

            <div>
              <Label className="text-xs text-gray-600">Y (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.position_data.y.toFixed(1)}
                onChange={(e) => setFormData(prev => ({ ...prev, position_data: { ...prev.position_data, y: parseFloat(e.target.value) || 0 } }))}
                onKeyDown={handleKeyPress}
                className={`mt-1 ${errors.y ? 'border-red-500' : ''}`}
              />
              {errors.y && (
                <p className="text-xs text-red-500 mt-1">{errors.y}</p>
              )}
            </div>

            <div>
              <Label className="text-xs text-gray-600">Width (%)</Label>
              <Input
                type="number"
                min="1"
                max="100"
                step="0.1"
                value={formData.position_data.width.toFixed(1)}
                onChange={(e) => setFormData(prev => ({ ...prev, position_data: { ...prev.position_data, width: parseFloat(e.target.value) || 1 } }))}
                onKeyDown={handleKeyPress}
                className={`mt-1 ${errors.width ? 'border-red-500' : ''}`}
              />
              {errors.width && (
                <p className="text-xs text-red-500 mt-1">{errors.width}</p>
              )}
            </div>

            <div>
              <Label className="text-xs text-gray-600">Height (%)</Label>
              <Input
                type="number"
                min="1"
                max="100"
                step="0.1"
                value={formData.position_data.height.toFixed(1)}
                onChange={(e) => setFormData(prev => ({ ...prev, position_data: { ...prev.position_data, height: parseFloat(e.target.value) || 1 } }))}
                onKeyDown={handleKeyPress}
                className={`mt-1 ${errors.height ? 'border-red-500' : ''}`}
              />
              {errors.height && (
                <p className="text-xs text-red-500 mt-1">{errors.height}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="pt-2 border-t">
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} className="flex-1">
            Save Changes
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

function generateTextSizeFrom(field_id: string) {
  return DEFAULT_FONT_SIZE;
}

